import { SidebarInset, SidebarProvider, Toaster, useSidebar } from "@nook/ui"
import { useNavigate } from "@tanstack/react-router"
import { createContext, useContext, useMemo, useState, type CSSProperties, type ReactNode } from "react"

import { AppSidebar } from "@/components/app-sidebar.js"
import { CaptureDialog } from "@/components/capture-dialog.js"
import { CommandPalette } from "@/components/command-palette.js"
import { useKeys } from "@/components/keys.js"
import { ShortcutsDialog } from "@/components/shortcuts-dialog.js"

type Overlay = "palette" | "shortcuts" | "capture"

const Shell = createContext<(overlay: Overlay) => void>(() => {})

/** Opens one of the shell's overlays from anywhere inside it. */
export const useOverlay = () => useContext(Shell)

/**
 * The chrome every protected screen renders inside: the sidebar, and the
 * overlays any screen can summon by key — the palette, the shortcut sheet and
 * capture. No auth check of its own; `_authed.tsx`'s `beforeLoad` did that.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const [overlay, setOverlay] = useState<Overlay | null>(null)
  const toggle = (to: Overlay) => (open: boolean) => setOverlay(open ? to : null)
  // Controlled, SidebarProvider leaves the cookie to us.
  const [sidebarOpen, setSidebarOpen] = useState(() => !document.cookie.includes("sidebar_state=false"))
  const saveSidebar = (open: boolean) => {
    setSidebarOpen(open)
    document.cookie = `sidebar_state=${open}; path=/; max-age=${60 * 60 * 24 * 7}`
  }

  return (
    <Shell.Provider value={setOverlay}>
      <SidebarProvider
        open={sidebarOpen}
        onOpenChange={saveSidebar}
        style={{ "--sidebar-width": "15rem", "--sidebar-width-icon": "3.5rem" } as CSSProperties}
      >
        <GlobalKeys open={setOverlay} />
        <AppSidebar />
        <SidebarInset className="h-svh min-w-0 overflow-y-auto">{children}</SidebarInset>
      </SidebarProvider>

      <CommandPalette open={overlay === "palette"} onOpenChange={toggle("palette")} />
      <ShortcutsDialog open={overlay === "shortcuts"} onOpenChange={toggle("shortcuts")} />
      <CaptureDialog open={overlay === "capture"} onOpenChange={toggle("capture")} />
      <Toaster position="bottom-right" />
    </Shell.Provider>
  )
}

function GlobalKeys({ open }: { open: (overlay: Overlay) => void }) {
  const navigate = useNavigate()
  const { toggleSidebar } = useSidebar()
  const ask = () => void navigate({ to: "/ask/$threadId", params: { threadId: "new" } })

  useKeys(
    useMemo(
      () => ({
        "$mod+k": () => open("palette"),
        "/": () => open("palette"),
        "[Shift]+?": () => open("shortcuts"),
        n: () => open("capture"),
        "Shift+n": () => void navigate({ to: "/n/$id", params: { id: "new" } }),
        "$mod+j": ask,
        "$mod+Shift+o": ask,
        "g l": () => void navigate({ to: "/" }),
        "g a": ask,
        "g s": () => void navigate({ to: "/settings" }),
        "[": toggleSidebar,
      }),
      [open, navigate, toggleSidebar],
    ),
  )

  return null
}
